import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { twoFactor, TwoFAStatus } from '../../services/twofa';
import { LoadingSpinner } from '../common/LoadingSpinner';

// Account -> Security: enroll in TOTP 2FA (QR + confirm code -> recovery codes)
// and turn it off (requires a current code). Login-gated model: once on, 2FA is
// asked for at sign-in, not per action.
export const TwoFactorSettings: React.FC = () => {
  const [status, setStatus] = useState<TwoFAStatus | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // enrollment
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  // disable
  const [disabling, setDisabling] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  const load = () =>
    twoFactor.status().then(setStatus).catch(() => setStatus({ enabled: false, recovery_codes_remaining: 0 }));
  useEffect(() => { load(); }, []);

  const beginSetup = async () => {
    setErr(''); setBusy(true);
    try { setSetup(await twoFactor.setup()); }
    catch (e: any) { setErr(e.response?.data?.detail || 'Could not start setup. Please try again.'); }
    finally { setBusy(false); }
  };

  const confirmEnable = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const res = await twoFactor.enable(enrollCode.trim());
      setRecoveryCodes(res.recovery_codes);
      setSetup(null); setEnrollCode('');
      await load();
    } catch (e2: any) { setErr(e2.response?.data?.detail || 'That code was not accepted. Try again.'); }
    finally { setBusy(false); }
  };

  const confirmDisable = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      await twoFactor.disable(disableCode.trim());
      setDisabling(false); setDisableCode(''); setRecoveryCodes(null);
      await load();
    } catch (e2: any) { setErr(e2.response?.data?.detail || 'That code was not accepted. Try again.'); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    if (recoveryCodes) navigator.clipboard?.writeText(recoveryCodes.join('\n')).catch(() => {});
  };

  return (
    <div className="auth-panel" style={{ marginTop: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h2 style={{ marginBottom: 0 }}>Two-factor authentication</h2>
        {status && (
          <span className={`auth-badge${status.enabled ? ' ok' : ''}`}>{status.enabled ? 'On' : 'Off'}</span>
        )}
      </div>

      {err && <div className="auth-error" role="alert" style={{ marginTop: 10 }}>{err}</div>}

      {status === null && <div style={{ padding: '10px 0' }}><LoadingSpinner size="sm" /></div>}

      {/* Recovery codes shown once, right after enabling */}
      {recoveryCodes && (
        <div>
          <p className="auth-note" style={{ textAlign: 'left', margin: '4px 0 12px', color: 'var(--champ)' }}>
            Two-factor authentication is on. Save these recovery codes somewhere safe — each works once if you
            lose your authenticator. <b>They won't be shown again.</b>
          </p>
          <div className="recovery-grid">
            {recoveryCodes.map((c) => <code key={c} className="recovery-code">{c}</code>)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" className="auth-btn auth-btn-ghost" style={{ width: 'auto' }} onClick={copyCodes}>Copy codes</button>
            <button type="button" className="auth-btn" style={{ width: 'auto' }} onClick={() => setRecoveryCodes(null)}>I've saved them</button>
          </div>
        </div>
      )}

      {/* Enrollment: QR + confirm code */}
      {!recoveryCodes && setup && (
        <form onSubmit={confirmEnable}>
          <p className="auth-note" style={{ textAlign: 'left', margin: '4px 0 12px' }}>
            Scan this with an authenticator app (Google Authenticator, Authy, 1Password…), then enter the 6-digit code it shows.
          </p>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ background: '#fff', padding: 12, borderRadius: 10, lineHeight: 0 }}>
              <QRCodeSVG value={setup.otpauth_uri} size={160} />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <p className="auth-label" style={{ marginBottom: 4 }}>Can't scan? Enter this key manually:</p>
              <code className="recovery-code" style={{ display: 'inline-block', wordBreak: 'break-all' }}>{setup.secret}</code>
              <div className="auth-field" style={{ marginTop: 14 }}>
                <label className="auth-label" htmlFor="enroll-code">Enter the code from your app</label>
                <input
                  id="enroll-code" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus required
                  className="auth-input" placeholder="123456"
                  value={enrollCode} onChange={(e) => setEnrollCode(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" className="auth-btn auth-btn-ghost" style={{ width: 'auto' }}
              onClick={() => { setSetup(null); setEnrollCode(''); setErr(''); }}>Cancel</button>
            <button type="submit" className="auth-btn" style={{ width: 'auto' }} disabled={busy || !enrollCode.trim()}>
              {busy ? <LoadingSpinner size="sm" /> : 'Turn on 2FA'}
            </button>
          </div>
        </form>
      )}

      {/* Enabled, resting state */}
      {!recoveryCodes && !setup && status?.enabled && (
        <>
          <p className="auth-note" style={{ textAlign: 'left', margin: '4px 0 4px' }}>
            You'll be asked for a code from your authenticator app each time you sign in.
            {' '}<span className="muted">{status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining === 1 ? '' : 's'} left.</span>
          </p>
          {!disabling ? (
            <button type="button" className="auth-btn auth-btn-ghost" style={{ width: 'auto', marginTop: 8 }} onClick={() => { setDisabling(true); setErr(''); }}>
              Turn off 2FA
            </button>
          ) : (
            <form onSubmit={confirmDisable} style={{ marginTop: 10 }}>
              <div className="auth-field">
                <label className="auth-label" htmlFor="disable-code">Enter a code to turn off 2FA</label>
                <input
                  id="disable-code" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus required
                  className="auth-input" placeholder="Authenticator or recovery code"
                  value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="auth-btn auth-btn-ghost" style={{ width: 'auto' }}
                  onClick={() => { setDisabling(false); setDisableCode(''); setErr(''); }}>Cancel</button>
                <button type="submit" className="auth-btn danger" style={{ width: 'auto' }} disabled={busy || !disableCode.trim()}>
                  {busy ? <LoadingSpinner size="sm" /> : 'Turn off 2FA'}
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {/* Disabled, resting state */}
      {!recoveryCodes && !setup && status && !status.enabled && (
        <>
          <p className="auth-note" style={{ textAlign: 'left', margin: '4px 0 4px' }}>
            Add a second step at sign-in with an authenticator app. Protects your account — including deleting it —
            even if your password is compromised.
          </p>
          <button type="button" className="auth-btn" style={{ width: 'auto', marginTop: 10 }} onClick={beginSetup} disabled={busy}>
            {busy ? <LoadingSpinner size="sm" /> : 'Set up two-factor authentication'}
          </button>
        </>
      )}
    </div>
  );
};
